import React from "react";
import { motion } from "framer-motion";
import {
  Shield,
  Building,
  Mail,
  Phone,
  MapPin,
  Globe,
  Lock,
  Eye,
  FileText,
  Users,
  Clock,
  AlertCircle,
  Cookie as CookieIcon,
  Settings,
  Monitor,
} from "lucide-react";

const Cookie = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true },
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            Cookie Policy
          </h1>
          <div className="bg-white rounded-lg shadow-lg p-6 text-left max-w-2xl mx-auto">
            <div className="space-y-2 text-sm text-gray-600">
              <p>
                <strong>Effective date:</strong> 24 April 2026
              </p>
              <p>
                <strong>Last updated:</strong> 24 April 2026
              </p>
              <p>
                <strong>Contact for this policy:</strong> privacy@notapaths.com
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Introduction */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <p className="text-gray-700 leading-relaxed mb-8">
            This Cookie Policy explains how{" "}
            <strong>JONOTA TRAVEL AGENCY LIMITED</strong> trading as "Notapaths"
            ("we", "us", "our") uses cookies and similar technologies on our
            websites, client portals, and emails. It works together with our
            Privacy Policy.
          </p>

          <div className="bg-gray-50 rounded-lg p-8 mb-8">
            <div className="space-y-3 text-gray-700">
              <p>
                <strong>Website / domain:</strong> notapaths.com
              </p>
              <p>
                <strong>UK registered office:</strong> 8a Hessel Street, Ground
                Floor, London, United Kingdom, E1 2LP
              </p>
              <p>
                <strong>London Office:</strong> 8a Hessel Street, Ground Floor,
                London, E1 2LP, UK · +44 7418 622985 · info@notapaths.com
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* What are cookies */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8 flex items-center">
            <CookieIcon className="w-8 h-8 mr-3 text-acBlack" strokeWidth={1} />
            1) What are cookies and similar technologies
          </h2>

          <div className="space-y-4 text-gray-700">
            <p>
              <strong>Cookies</strong> are small text files placed on your
              device when you visit a website.
            </p>
            <p>
              <strong>Pixels and tags</strong> are small pieces of code that
              help us understand usage and the success of features or emails.
            </p>
            <p>
              <strong>Local storage and session storage</strong> allow a site to
              store data in your browser.
            </p>
            <p>
              <strong>SDKs</strong> are similar tools used in mobile apps.
            </p>
            <p>
              These technologies help a site function, keep you signed in,
              remember choices, measure performance and, where enabled,
              personalise content or ads.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Types of cookies */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            2) Types of cookies we use
          </h2>
          <p className="text-gray-700 mb-8">
            We group cookies by purpose. You can manage non-essential categories
            in the Cookie Settings panel.
          </p>

          <div className="space-y-6">
            {[
              {
                type: "Strictly necessary",
                description:
                  "Required for the site to work. Examples: page navigation, loading resources, security, forms, fraud prevention, cookie consent storage.",
                note: "Always active because they are essential.",
                icon: <Shield className="w-6 h-6" strokeWidth={1} />,
                color: "red",
              },
              {
                type: "Performance and analytics",
                description:
                  "Help us understand how the site is used so we can improve it. Examples: page views, time on page, error logs, campaign attribution. Data is aggregated where possible. IPs may be truncated or anonymised when configured.",
                icon: <Monitor className="w-6 h-6" strokeWidth={1} />,
                color: "blue",
              },
              {
                type: "Functionality",
                description:
                  "Remember your preferences. Examples: language, region, saved form inputs, accessibility settings.",
                icon: <Settings className="w-6 h-6" strokeWidth={1} />,
                color: "green",
              },
              {
                type: "Advertising and social media",
                description:
                  "Show or measure ads, build audiences, or allow social sharing. May track you across sites. We only use these with your consent where required by law.",
                icon: <Eye className="w-6 h-6" strokeWidth={1} />,
                color: "purple",
              },
              {
                type: "Email tracking pixels",
                description:
                  "If you opt in to emails, we may use pixels to measure opens and link clicks. You can disable images in your email client to limit this.",
                icon: <Mail className="w-6 h-6" strokeWidth={1} />,
                color: "yellow",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-sm border"
              >
                <div className="flex items-start">
                  <div className="bg-yellow-100 p-2 rounded-lg mr-4 text-acBlack flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-acBlack mb-2">
                      {item.type}
                    </h3>
                    <p className="text-gray-700 text-sm mb-2">
                      {item.description}
                    </p>
                    {item.note && (
                      <p className="text-red-600 text-sm font-medium">
                        {item.note}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* First-party vs third-party */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            3) First-party vs third-party
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-acBlack mb-3">
                First-party cookies
              </h3>
              <p className="text-gray-700">are set by notapaths.com.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-acBlack mb-3">
                Third-party cookies
              </h3>
              <p className="text-gray-700">
                are set by service providers, for example analytics or embedded
                video. Third parties may treat the data they collect as personal
                data under applicable law.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Duration */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            4) Duration
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm border">
              <h3 className="font-semibold text-acBlack mb-3">
                Session cookies
              </h3>
              <p className="text-gray-700">
                delete when you close your browser.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border">
              <h3 className="font-semibold text-acBlack mb-3">
                Persistent cookies
              </h3>
              <p className="text-gray-700">
                stay for a set time or until you delete them. We set reasonable
                lifetimes and review them periodically.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Legal basis */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            5) Legal basis for cookies
          </h2>
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-acBlack mb-2">
                Strictly necessary
              </h3>
              <p className="text-gray-700 text-sm">
                our legitimate interests in running a secure and functional site
                (and/or performance of a contract where applicable).
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-acBlack mb-2">
                Performance, functionality, advertising, social
              </h3>
              <p className="text-gray-700 text-sm">
                your consent where required under UK and EU law. In some
                jurisdictions we may rely on legitimate interests for limited,
                privacy-protective analytics. You can withdraw consent at any
                time in Cookie Settings.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Your choices */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            6) Your choices
          </h2>

          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                A) Cookie Settings panel
              </h3>
              <p className="text-gray-700">
                Click Cookie Settings on our site to review, accept, or reject
                categories. Your choices apply to the browser and device you
                use. If you clear cookies or use a different device or browser,
                you will need to set preferences again.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                B) Browser controls
              </h3>
              <p className="text-gray-700">
                Most browsers let you block or delete cookies under Privacy or
                Security settings. Blocking strictly necessary cookies may break
                site features.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                C) Do Not Track and Global Privacy Control
              </h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  <strong>Do Not Track (DNT):</strong> there is no standard we
                  can apply consistently, so we do not respond to DNT.
                </p>
                <p>
                  <strong>Global Privacy Control (GPC):</strong> where required
                  by law, we treat GPC as a request to opt out of the sale or
                  sharing of personal information for cross-context behavioural
                  advertising. You can still adjust Cookie Settings for more
                  granular control.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-acBlack mb-4">
                D) Advertising choices
              </h3>
              <p className="text-gray-700">
                You can opt out of interest-based advertising from many
                providers at network opt-out pages in your country (e.g.,
                youradchoices.com or youronlinechoices.eu). This limits targeted
                ads but does not remove ads.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Common cookies table */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            7) Cookies we commonly use
          </h2>
          <p className="text-gray-700 mb-8">
            Replace or remove rows to reflect the tools you actually deploy.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-3 text-left font-semibold text-acBlack">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-acBlack">
                    Name / Example
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-acBlack">
                    Purpose
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-acBlack">
                    Typical lifetime
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-acBlack">
                    Party
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[
                  [
                    "Strictly necessary",
                    "cookie_consent_status",
                    "remembers your consent choices",
                    "6–12 months",
                    "first-party",
                  ],
                  [
                    "Strictly necessary",
                    "__cf_bm (or similar)",
                    "bot management/security (if using a CDN)",
                    "up to 30 minutes",
                    "third-party (CDN)",
                  ],
                  [
                    "Strictly necessary",
                    "session_id",
                    "keeps you signed in / session state",
                    "session",
                    "first-party",
                  ],
                  [
                    "Performance & analytics",
                    "_ga, _gid, _gat or GA4 equivalents",
                    "Google Analytics identifiers/throttling",
                    "1 minute to 24 months",
                    "third-party (Google)",
                  ],
                  [
                    "Performance & analytics",
                    "_clck, _clsk",
                    "Microsoft Clarity session & clickstream",
                    "session to 12 months",
                    "third-party (Microsoft)",
                  ],
                  [
                    "Performance & analytics",
                    "log_level / error_state",
                    "error tracking & stability",
                    "session to 12 months",
                    "first-party / third-party (e.g., Sentry)",
                  ],
                  [
                    "Functionality",
                    "locale / lang",
                    "remembers language/region",
                    "6–12 months",
                    "first-party",
                  ],
                  [
                    "Functionality",
                    "remember_me",
                    "saves login/form preference",
                    "30 days–12 months",
                    "first-party",
                  ],
                  [
                    "Advertising & social",
                    "_fbp",
                    "Facebook pixel for measurement",
                    "~3 months",
                    "third-party (Meta)",
                  ],
                  [
                    "Advertising & social",
                    "_tt_enable_cookie, _ttp",
                    "TikTok pixel",
                    "up to 13 months",
                    "third-party (TikTok)",
                  ],
                  [
                    "Advertising & social",
                    "li_fat_id",
                    "LinkedIn Insights",
                    "up to 6 months",
                    "third-party (LinkedIn)",
                  ],
                  [
                    "Advertising & social / embeds",
                    "yt_innertube_*, VISITOR_INFO1_LIVE",
                    "YouTube embed preferences/measurement",
                    "up to 6 months",
                    "third-party (Google)",
                  ],
                  [
                    "Email pixels",
                    "ESP open/click trackers",
                    "measure engagement in emails",
                    "per email",
                    "third-party ESP",
                  ],
                ].map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700 font-medium">
                      {row[0]}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                      {row[1]}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row[2]}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row[3]}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row[4]}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-gray-600 text-sm mt-4">
            We may update this list as our site changes. Material changes will
            be reflected in this policy.
          </p>
        </div>
      </motion.section>

      {/* Consent collection */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            8) When consent is required and how we collect it
          </h2>
          <div className="space-y-4 text-gray-700">
            <p>
              In the UK and EEA we obtain consent before placing or reading
              non-essential cookies.
            </p>
            <p>
              Our banner presents clear options: <strong>Accept all</strong>,{" "}
              <strong>Reject non-essential</strong>, and{" "}
              <strong>Manage settings</strong>.
            </p>
            <p>
              We record consent choices and the timestamp to demonstrate
              compliance.
            </p>
            <p>
              If you continue browsing after rejecting non-essential cookies,
              only strictly necessary cookies will be used.
            </p>
          </div>
        </div>
      </motion.section>

      {/* California notes */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            9) Special notes for California and certain U.S. states
          </h2>
          <div className="space-y-4 text-gray-700">
            <p>
              Some cookies used for advertising or analytics may be considered a
              sale or sharing of personal information under state laws such as
              the California Consumer Privacy Act (as amended by CPRA).
            </p>
            <p>
              To opt out of sale or sharing, use the{" "}
              <strong>Do Not Sell or Share My Personal Information</strong> link
              in the footer or adjust the Cookie Settings panel. We also honour
              GPC signals where required.
            </p>
            <p>
              Your rights are described in our Privacy Policy and may include
              access, deletion, correction, and the right to limit the use of
              sensitive personal information.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Data collection */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            10) Data we collect through cookies
          </h2>
          <p className="text-gray-700">
            Depending on your settings, cookies may collect: device and browser
            type, operating system, IP address, general location, pages viewed,
            buttons clicked, time spent, referring URLs, and campaign
            identifiers. We use aggregation and de-identification where
            possible, and retention periods that match the purpose.
          </p>
        </div>
      </motion.section>

      {/* Retention */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8 flex items-center">
            <Clock className="w-8 h-8 mr-3 text-acBlack" strokeWidth={1} />
            11) Retention
          </h2>
          <p className="text-gray-700">
            We keep cookie data only as long as needed for each purpose.
            Persistent cookies have fixed lifetimes shown in the table.
            Analytics data is retained according to our analytics provider
            settings and our data retention policy.
          </p>
        </div>
      </motion.section>

      {/* Children */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            12) Children
          </h2>
          <p className="text-gray-700">
            Our site is not directed to children. We process children's
            information only when necessary for family immigration services and
            only through parents or legal guardians, not for advertising.
          </p>
        </div>
      </motion.section>

      {/* Updates */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            13) Updates to this policy
          </h2>
          <p className="text-gray-700">
            We may update this Cookie Policy from time to time. Material changes
            will be posted on this page with a new effective date. If required
            by law, we will ask for consent again.
          </p>
        </div>
      </motion.section>

      {/* Contact */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            14) How to contact us
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm border">
              <h3 className="font-semibold text-acBlack mb-3">Email</h3>
              <a
                href="mailto:privacy@notapaths.com"
                className="text-acBlack hover:text-gray-700 transition-colors"
              >
                privacy@notapaths.com
              </a>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm border">
              <h3 className="font-semibold text-acBlack mb-3">UK postal</h3>
              <p className="text-gray-700 text-sm">
                JONOTA TRAVEL AGENCY LIMITED (t/a Notapaths), 8a Hessel Street,
                Ground Floor, London, United Kingdom, E1 2LP
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Optional banner copy */}
      <motion.section className="py-16 px-6 bg-blue-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            15) Optional banner and settings copy
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-acBlack mb-3">Banner title</h3>
              <p className="text-gray-700">Your privacy choices</p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-acBlack mb-3">Banner text</h3>
              <p className="text-gray-700">
                We use cookies to run the site, improve performance, and, if you
                agree, to personalise content and measure marketing. You can
                make changes at any time in Cookie Settings.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-acBlack mb-3">Buttons</h3>
              <p className="text-gray-700">
                Accept all • Reject non-essential • Cookie Settings
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-acBlack mb-3">
                Settings categories and examples
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>
                  <strong>Strictly necessary:</strong> security, load balancing,
                  form submission
                </li>
                <li>
                  <strong>Performance:</strong> analytics to improve site
                  features
                </li>
                <li>
                  <strong>Functionality:</strong> language, saved preferences
                </li>
                <li>
                  <strong>Advertising and social:</strong> ad measurement,
                  audiences, share buttons
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-acBlack mb-3">Footer links</h3>
              <p className="text-gray-700">
                Cookie Settings • Cookie Policy • Do Not Sell or Share My
                Personal Information
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Implementation tips */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack mb-8">
            16) Implementation tips for your developer
          </h2>

          <div className="space-y-4 text-gray-700">
            <p>
              • Load strictly necessary scripts by default. Gate all other tags
              behind consent categories.
            </p>
            <p>
              • Delay firing of analytics/advertising tags until consent is
              recorded (e.g., Google Consent Mode v2 / equivalent).
            </p>
            <p>
              • Store consent in a first-party cookie and, where relevant, in
              your CMP and ad/analytics tools.
            </p>
            <p>
              • Honour GPC signals by mapping them to an opt-out state at page
              load.
            </p>
            <p>
              • Provide a visible Cookie Settings link on every page (header or
              footer).
            </p>
            <p>
              • Maintain a changelog of vendors and cookie lifetimes; review at
              least quarterly.
            </p>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default Cookie;
